import React, { useState } from 'react';

import { I18N_NAMESPACES } from 'i18n';
import { useTranslation } from 'i18n/hooks';
import { useCountries } from 'i18n/hooks/useCountries';
import { SpacedContainer } from 'revibe-ui';

import useScrollPosition from '@react-hook/window-scroll';

import { ShopPage } from 'modules/common/components/ShopPage';
import { isBrowserEnv } from 'modules/common/utils/env';
import { SellerPreview } from 'modules/seller/components/SellerPreview';

import { Input } from 'ui/components/Input';
import { Tag } from 'ui/components/Tag';
import { MarketplaceAPI } from 'revibe-api';

type Props = { sellers: MarketplaceAPI.Seller[]; countries: string[] };

export const BrandsPage = ({ sellers, countries }: Props) => {
  const { getCountryByCode } = useCountries();
  const { tu } = useTranslation(I18N_NAMESPACES.SELLER);
  const scrollY = useScrollPosition(60);
  const [searchString, setSearchString] = useState('');
  const [country, setCountry] = useState('');

  return (
    <ShopPage
      title={tu('brands', I18N_NAMESPACES.PAGE_TITLES)}
      isShopPage
      description={`${tu('list-heading')}, ${tu('list-text1')} ${tu(
        'list-text2'
      )}`}
    >
      <SpacedContainer className="w-full">
        <div className="overflow-hidden">
          <div className="mx-auto max-w-2xl px-4 py-10 text-center">
            <h1 className="font-formula text-4xl uppercase lg:text-6xl">
              {tu('list-heading')}
            </h1>
          </div>
          <div className="mx-auto max-w-lg px-4">
            <Input
              value={searchString}
              onChange={(e) => {
                setSearchString(e.currentTarget.value);
                if (isBrowserEnv()) {
                  window.scrollTo(0, scrollY + 1);
                }
              }}
              placeholder={`${tu('search', 'common')}...`}
            />
          </div>
          <SpacedContainer
            type="horizontal"
            className="mx-auto w-fit justify-start px-4 py-8"
          >
            <div onClick={() => setCountry('')} className="cursor-pointer">
              <Tag
                type={country === '' ? 'dark' : 'neutral'}
                className="whitespace-nowrap "
              >
                All
              </Tag>
            </div>
            {countries.map((c) => (
              <div
                key={c}
                onClick={() => setCountry(c)}
                className="cursor-pointer"
              >
                <Tag
                  type={country === c ? 'dark' : 'neutral'}
                  className="whitespace-nowrap "
                >
                  {getCountryByCode(c)}
                </Tag>
              </div>
            ))}
          </SpacedContainer>
        </div>
        <div className="grid w-full gap-4 gap-y-8 px-4 pb-10 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4">
          {sellers
            .filter((seller) => {
              if (!searchString) {
                return true;
              }
              return seller.name
                .toLowerCase()
                .includes(searchString.toLowerCase());
            })
            .filter((seller) => {
              if (!country) {
                return true;
              }
              return seller.country.includes(country);
            })
            .map((seller) => (
              <SellerPreview key={seller.id} seller={seller} />
            ))}
        </div>
      </SpacedContainer>
    </ShopPage>
  );
};
